import { useCallback, useEffect, useState } from 'react';
import { ArrowCounterClockwise, ArrowsClockwise, FloppyDisk, UserCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ClientWorkspaceControls({ api, project, onInherited }) {
  const [brain, setBrain] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [busy, setBusy] = useState('');
  const [brandKit, setBrandKit] = useState({});
  const [clientName, setClientName] = useState('');
  const [brandName, setBrandName] = useState('');

  const load = useCallback(async () => {
    if (!project?.id || !project?.client_brain_id) {
      setBrain(null);
      setWorkspace(null);
      return;
    }
    try {
      const [{ data: effective }, { data: workspaceData }, { data: loadedBrandKit }] = await Promise.all([
        api.get(`/projects/${project.id}/effective-brain`),
        api.get(`/client-brains/${project.client_brain_id}/workspace`),
        api.get(`/brand-kit/${project.id}`),
      ]);
      const nextBrain = effective?.client_brain || null;
      const nextBrandKit = loadedBrandKit || {};
      setBrain(nextBrain);
      setWorkspace(workspaceData || null);
      setBrandKit(nextBrandKit);
      setClientName(nextBrain?.name || '');
      setBrandName(nextBrandKit.brand_name || '');
    } catch {
      setBrain(null);
      setWorkspace(null);
    }
  }, [api, project?.id, project?.client_brain_id]);

  useEffect(() => { load(); }, [load]);

  const saveIdentity = async () => {
    const nextClientName = clientName.trim();
    const nextBrandName = brandName.trim();
    if (!nextClientName || !nextBrandName) {
      toast.error('Inserisci sia il nome cliente sia il nome del brand');
      return;
    }
    setBusy('identity');
    try {
      const [{ data: updatedBrain }] = await Promise.all([
        api.put(`/client-brains/${project.client_brain_id}`, { name: nextClientName }),
        api.post('/brand-kit/save', {
          project_id: project.id,
          brand_name: nextBrandName,
          brand_brief: brandKit.brand_brief || '',
        }),
      ]);
      setBrain(updatedBrain || { ...(brain || {}), name: nextClientName });
      setBrandKit(previous => ({ ...previous, brand_name: nextBrandName }));
      setClientName(nextClientName);
      setBrandName(nextBrandName);
      toast.success('Identità slide salvata: Client name e Brand name sono ora distinti');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  };

  const promote = async () => {
    setBusy('promote');
    try {
      const { data } = await api.post(`/projects/${project.id}/client-workspace/promote`);
      setWorkspace(prev => ({
        ...(prev || {}),
        workspace_defaults: data.workspace_defaults,
        workspace_revision: data.workspace_revision,
      }));
      toast.success('Setup V3 e profilo salvati nel cliente');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  };

  const inherit = async () => {
    if (!window.confirm('Ripristinare il setup salvato del cliente? Template V3, Design, ToV e Brand disponibili sostituiranno i valori locali corrispondenti.')) return;
    setBusy('inherit');
    try {
      const { data } = await api.post(`/projects/${project.id}/client-workspace/inherit`);
      await load();
      toast.success(data.inherited?.length ? `Profilo cliente applicato: ${data.inherited.join(', ')}` : 'Il cliente non ha ancora impostazioni riutilizzabili');
      onInherited?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  };

  const resetDesign = async () => {
    if (!window.confirm('Azzerare completamente il Design V3 di questo progetto? Tutti i template e le modifiche correnti verranno eliminati. Il profilo cliente salvato non verrà modificato.')) return;
    setBusy('reset');
    try {
      await api.post(`/projects/${project.id}/design/v3/reset`);
      toast.success('Design V3 azzerato: puoi ripartire da un canvas vuoto');
      onInherited?.({ reset: true });
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  };

  if (!project?.client_brain_id) {
    return (
      <div className="rounded-xl border border-[var(--border-color)] p-5" data-testid="client-workspace-controls">
        <div className="flex items-center gap-2"><UserCircle size={20} className="text-[var(--accent-purple)]"/><h3 className="font-semibold">Gestione cliente</h3></div>
        <p className="text-sm text-[var(--text-muted)] mt-2">Questo progetto non è collegato a un Client Brain. Il setup resta quindi solo nel progetto corrente.</p>
      </div>
    );
  }

  const defaults = workspace?.workspace_defaults || {};
  const available = [
    Array.isArray(defaults.design_v3_templates) && defaults.design_v3_templates.length ? `Template V3 (${defaults.design_v3_templates.length})` : null,
    defaults.design_dna_v2 && Object.keys(defaults.design_dna_v2).length ? 'Font / Design base' : null,
    defaults.tov && Object.keys(defaults.tov).length ? 'Tone of Voice' : null,
    defaults.brand_kit && Object.keys(defaults.brand_kit).length ? 'Brand' : null,
  ].filter(Boolean);

  return (
    <div className="rounded-xl border border-[var(--border-color)] px-3 py-2.5 space-y-2" data-testid="client-workspace-controls" data-layout="compact">
      <div className="flex items-center gap-2 min-w-0">
        <UserCircle size={18} className="text-[var(--accent-purple)] shrink-0" />
        <p className="text-[9px] uppercase tracking-[.16em] text-[var(--accent-purple)] font-semibold shrink-0">Cliente</p>
        <h3 className="text-sm font-semibold truncate">{brain?.name || project.client_brain_id}</h3>
        <span className="ml-auto text-[9px] text-[var(--text-muted)] hidden sm:inline">Profilo rev. {workspace?.workspace_revision || 0}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[minmax(150px,1fr)_minmax(150px,1fr)_auto] gap-2 items-end" data-testid="v3-slide-identity">
        <label className="block"><span className="sr-only">Client name</span><input className="input-dark text-xs w-full !py-2" value={clientName} onChange={event => setClientName(event.target.value)} placeholder="Client name" aria-label="Client name" data-testid="v3-client-name" /></label>
        <label className="block"><span className="sr-only">Brand name</span><input className="input-dark text-xs w-full !py-2" value={brandName} onChange={event => setBrandName(event.target.value)} placeholder="Brand name" aria-label="Brand name" data-testid="v3-brand-name" /></label>
        <button type="button" className="btn-gradient !px-3 !py-2 text-[10px] justify-center" title="Salva Client name e Brand name" onClick={saveIdentity} disabled={!!busy} data-testid="v3-save-identity"><FloppyDisk size={13} /> <span className="hidden md:inline">{busy === 'identity' ? 'Salvo…' : 'Salva identità'}</span></button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="client-workspace-compact-actions">
        <span className="text-[9px] text-[var(--text-muted)] mr-auto truncate max-w-full sm:max-w-[38%]" title={available.length ? available.join(' · ') : 'Nessun setup salvato'}>{available.length ? available.join(' · ') : 'Nessun setup cliente salvato'}</span>
        <button type="button" className="btn-ghost !px-2.5 !py-1.5 text-[10px]" onClick={promote} disabled={!!busy} data-testid="client-workspace-promote" title="Salva il setup corrente nel profilo cliente"><FloppyDisk size={12}/>{busy === 'promote' ? 'Salvo…' : 'Salva setup'}</button>
        <button type="button" className="btn-ghost !px-2.5 !py-1.5 text-[10px]" onClick={inherit} disabled={!!busy} data-testid="client-workspace-inherit" title="Ripristina il setup salvato nel profilo cliente"><ArrowsClockwise size={12}/>{busy === 'inherit' ? 'Ripristino…' : 'Ripristina'}</button>
        <button type="button" className="btn-ghost !px-2.5 !py-1.5 text-[10px] text-rose-300" onClick={resetDesign} disabled={!!busy} data-testid="client-workspace-reset-design" title="Azzera completamente il Design V3"><ArrowCounterClockwise size={12}/>{busy === 'reset' ? 'Azzero…' : 'Reset'}</button>
      </div>
    </div>
  );}
